<script lang="ts">
  import Chart from '../charts/Chart.svelte';
  import Readout from '../charts/Readout.svelte';
  import { shiftDay } from '../../utils/insights';
  import { hourLabel, sleepTime, STAGES, type HealthSleep, type SleepStage } from '../../utils/health';
  import { t, time } from '../../i18n/index.ts';

  // SleepTimeline is a day's sleep as one bar from 6 PM the evening before to
  // noon (spec 007 FR-019), each stage its own color, or the whole
  // session as Asleep when the source gave no stages, marked every 3 hours
  // in the shared chart language (spec 010 FR-033). Hovering or tapping
  // reads the stage at that time; the readout totals each stage with its
  // color when nothing is picked, and the sr-only table lists every span.
  let { date, sleep }: { date: string; sleep: HealthSleep[] } = $props();

  // STEPS are the 5-minute stops the guide moves between, 18 hours' worth.
  const STEPS = 18 * 12;

  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let shown = $derived(hover ?? at);

  let start = $derived(new Date(`${shiftDay(date, -1)}T18:00:00`).getTime());
  let end = $derived(new Date(`${date}T12:00:00`).getTime());

  let spans = $derived(sleep.flatMap((s) =>
    s.stages.length ? s.stages.map((st) => ({ stage: st.stage, from: st.from, to: st.to })) : [{ stage: 'asleep' as SleepStage, from: s.started_at, to: s.ended_at }],
  ));
  let inBar = $derived(spans
    .map((sp) => ({ ...sp, a: Math.max(start, Date.parse(sp.from)), b: Math.min(end, Date.parse(sp.to)) }))
    .filter((sp) => sp.b > sp.a));
  let totals = $derived(STAGES.map((st) => ({
    ...st,
    s: spans.filter((sp) => sp.stage === st.stage).reduce((sum, sp) => sum + (Date.parse(sp.to) - Date.parse(sp.from)) / 1000, 0),
  })).filter((st) => st.s > 0));
  const pct = (t: number) => ((t - start) / (end - start)) * 100;
  const colorOf = (s: SleepStage) => STAGES.find((x) => x.stage === s)?.color ?? 'var(--theme-blue)';
  const nameOf = (s: SleepStage) => STAGES.find((x) => x.stage === s)?.name ?? s;
  const positions = Array.from({ length: STEPS + 1 }, (_, i) => i / STEPS);
  const ticks = [18, 21, 0, 3, 6, 9, 12].map((h, i) => ({ at: i / 6, text: hourLabel(h) }));

  // reading is the time at the guide and the stage then: "1:20 AM: Deep".
  let reading = $derived.by(() => {
    if (shown == null) return '';
    const moment = start + (shown / STEPS) * (end - start);
    const sp = inBar.find((x) => x.a <= moment && moment < x.b);
    return t('healthStageAt', { time: time(new Date(moment)), stage: sp ? nameOf(sp.stage) : t('healthNotAsleep') });
  });
</script>

<div class="space-y-2">
  <Readout title={reading || t('healthHoverStage')} idle={!reading} items={totals.map((st) => ({ label: st.name, value: sleepTime(st.s), color: st.color, mark: 'bar' }))} />
  <Chart lo={0} hi={1} xTicks={ticks} height="h-10" {positions} bind:at bind:hover
    label={t('healthSleepChartLabel', { start: hourLabel(18) })} valueText={reading || t('healthNoTimePicked')} step={3}>
    {#snippet overlay()}
      <div class="absolute inset-0 rounded-sm overflow-hidden" style="background: color-mix(in srgb, var(--theme-blue) 10%, transparent);" aria-hidden="true">
        {#each inBar as sp, k (k)}
          <span class="absolute inset-y-0" style="left: {pct(sp.a)}%; width: {pct(sp.b) - pct(sp.a)}%; background: {colorOf(sp.stage)};"></span>
        {/each}
      </div>
    {/snippet}
  </Chart>
</div>
<table class="sr-only">
  <caption>{t('healthSleepStages')}</caption>
  <thead><tr><th>{t('healthStageColumn')}</th><th>{t('healthFrom')}</th><th>{t('healthTo')}</th></tr></thead>
  <tbody>
    {#each spans as sp, k (k)}
      <tr><td>{nameOf(sp.stage)}</td><td>{time(new Date(sp.from))}</td><td>{time(new Date(sp.to))}</td></tr>
    {/each}
  </tbody>
</table>
